import React, { FC, useMemo, useState } from 'react';
import { Button, Segmented } from 'antd';
import {
  CircleDot,
  Flag,
  List,
  Pencil,
  Tag as TagIcon,
  User as UserIcon
} from 'lucide-react';
import Avatar from '../../../../../../shared/Avatar';
import { Log } from '../../../../../models/Task';
import { Comment as CommentType } from '../../../../../models/Comment';
import { getLangKey } from '../../../../../models/Language';
import { useCommentsState } from '../../../../../../providers/Comments';
import Comment from '../Comments/CommentStream/Comment';
import * as translations from '../strings';
import ActivityEntry, { activityIconType } from './ActivityEntry';
import {
  buildCommentStream,
  ChangeType,
  StreamEntry
} from './buildCommentStream';
import {
  readStreamFilter,
  StreamFilter,
  writeStreamFilter
} from './streamFilter';
import styles from './CommentTimeline.module.css';
import streamStyles from '../Comments/CommentStream/styles.module.css';

const strings = translations[getLangKey()];
const PAGE_SIZE = 100;

const ICONS: Record<ChangeType | 'mixed', typeof List> = {
  status: CircleDot,
  severity: Flag,
  assignee_add: UserIcon,
  assignee_remove: UserIcon,
  description: Pencil,
  title: Pencil,
  created: Pencil,
  other: List,
  mixed: List
};

type Props = {
  logs: Log[];
  logsLoading: boolean;
  showDescriptionChanges: boolean;
};

const CommentTimeline: FC<Props> = ({
  logs,
  logsLoading,
  showDescriptionChanges
}) => {
  const {
    comments,
    commentsLoading,
    canViewPrivateComments,
    canViewCommitComment,
    page,
    setPage,
    getContainer
  } = useCommentsState();
  const [filter, setFilter] = useState<StreamFilter>(readStreamFilter);

  const visibleComments = useMemo(
    () =>
      (comments || []).filter(
        comment =>
          (canViewPrivateComments || !comment.isPrivate) &&
          (!comment.commitUrl || canViewCommitComment)
      ),
    [comments, canViewPrivateComments, canViewCommitComment]
  );

  const entries = useMemo(
    () =>
      buildCommentStream(
        filter === 'activity' ? [] : visibleComments,
        filter === 'comments' ? [] : logs,
        { groupActivity: filter === 'all' }
      ),
    [filter, visibleComments, logs]
  );

  const visibleEntries = entries.slice(0, page * PAGE_SIZE);
  const showLoader =
    (filter !== 'activity' && commentsLoading) ||
    (filter === 'activity' && logsLoading && logs.length === 0);

  const selectFilter = (next: StreamFilter) => {
    setFilter(next);
    writeStreamFilter(next);
    if (next !== filter) setPage(1);
  };

  const renderComment = (comment: CommentType) => (
    <Comment
      id={comment.id}
      userId={comment.userId}
      commitUrl={comment.commitUrl}
      text={comment.text}
      commentHtml={comment.commentHtml}
      createdAt={comment.createdAt}
      isPrivate={comment.isPrivate}
      commenter={comment.user}
      readByUsers={comment.readByUsers}
      reactions={comment.reactions}
      hideAvatar
    />
  );

  const renderActivityIcon = (
    entry: Extract<StreamEntry<CommentType>, { kind: 'activity' }>
  ) => {
    const isTaskCreation =
      entry.phrases.length === 1 &&
      entry.phrases[0].lead.startsWith('created task');
    const isStatusChange =
      entry.phrases.length === 1 &&
      entry.phrases[0].lead.startsWith('set status to');
    const isTagChange =
      entry.phrases.length === 1 &&
      /\btag(s)?\b/.test(entry.phrases[0].lead);
    const isAssigneeChange =
      entry.phrases.length === 1 &&
      /\bassignees?\b/.test(entry.phrases[0].lead);
    const Icon = isTaskCreation
      ? Pencil
      : isStatusChange
        ? CircleDot
      : isTagChange
        ? TagIcon
        : isAssigneeChange
          ? UserIcon
        : ICONS[activityIconType(entry.phrases.map(phrase => phrase.type))];
    return (
      <span className={styles.activityIcon} aria-hidden>
        <Icon size={14} strokeWidth={2} />
      </span>
    );
  };

  return (
    <div>
      <div className={styles.filterRow}>
        <Segmented
          size="small"
          value={filter}
          aria-label="Comment stream filter"
          onChange={value => selectFilter(value as StreamFilter)}
          options={[
            { label: strings.filterAll, value: 'all' },
            { label: strings.filterComments, value: 'comments' },
            { label: strings.filterActivity, value: 'activity' }
          ]}
        />
      </div>
      {showLoader ? (
        <div className={streamStyles.loaderContainer}>
          <Button type="link" loading className={styles.loader}>
            {filter === 'activity'
              ? strings.loadingActivity
              : strings.loadingComments}
          </Button>
        </div>
      ) : (
        <div
          className={styles.timeline}
          onScroll={event => event.stopPropagation()}
        >
          {visibleEntries.map((entry, index) => (
            <div
              key={entry.id}
              className={
                entry.kind === 'activity'
                  ? `${styles.row} ${styles.activityRow}${
                      index === visibleEntries.length - 1
                        ? ` ${styles.lastRow}`
                        : ''
                    }`
                  : `${styles.row}${
                      index === visibleEntries.length - 1
                        ? ` ${styles.lastRow}`
                        : ''
                    }`
              }
            >
              <div className={styles.gutter}>
                <div className={styles.marker}>
                  {entry.kind === 'comment' ? (
                    <Avatar size="small" member={entry.comment.user} />
                  ) : (
                    renderActivityIcon(entry)
                  )}
                </div>
              </div>
              <div
                className={
                  entry.kind === 'activity'
                    ? styles.activityBody
                    : styles.content
                }
              >
                {entry.kind === 'comment' ? (
                  renderComment(entry.comment)
                ) : (
                  <ActivityEntry
                    entry={entry}
                    showDescriptionChanges={showDescriptionChanges}
                    getContainer={getContainer}
                  />
                )}
              </div>
            </div>
          ))}
          {filter !== 'comments' && logsLoading && (
            <div className={streamStyles.loaderContainer}>
              <Button type="link" loading className={styles.loader}>
                {strings.loadingActivity}
              </Button>
            </div>
          )}
          {page * PAGE_SIZE < entries.length && (
            <div className={streamStyles.showMoreContainer}>
              <Button
                type="link"
                className={streamStyles.showMoreButton}
                onClick={() => setPage(page + 1)}
              >
                {strings.showMore}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentTimeline;
